import { Box, Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useGetLocalityNames } from '../../../../api/localities'
import { useGetAllResources } from '../../../../api/task-resource'
import { useGetTimeReportTypes } from '../../../../api/time-report-type'
import BasicDatePicker from '../../../../components/DateTimePicker'
import { AppForm, FormSelectField, FormSubmitButton } from '../../../../components/FormCore'
import { useCustomerCtx } from '../../../../hooks/context-hooks'
import { useZodForm } from '../../../../hooks/zod-form'
import { APIResultCount } from 'src/constant'
import { useMemo } from 'react'

const TimeReportFilter = ({ onChange }) => {
  const { customer } = useCustomerCtx()
  const { t } = useTranslation(['time-reports', 'task', 'time-report-types', 'common'])
  const { data: localities } = useGetLocalityNames(customer?.id)
  const { data: resources } = useGetAllResources(customer?.id, 0, APIResultCount.Max)
  const { data: timeReportTypes } = useGetTimeReportTypes(0, APIResultCount.Max)
  const sortedLocalities = useMemo(
    () => [...(localities ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [localities],
  )
  const sortedResources = useMemo(
    () => [...(resources?.items ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [resources],
  )
  const sortedTimeReportTypes = useMemo(
    () => [...(timeReportTypes?.items ?? [])].sort((a, b) => a.typeName.localeCompare(b.typeName)),
    [timeReportTypes],
  )

  const timeReportsFilterSchema = z
    .object({
      locality: z
        .array(
          z.object({
            id: z.number(),
          }),
        )
        .nullable(),
      resource: z
        .array(
          z.object({
            id: z.number(),
          }),
        )
        .nullable(),
      timeReportType: z
        .array(
          z.object({
            id: z.number(),
          }),
        )
        .nullable(),
      startDate: z.date().optional().nullable(),
      endDate: z.date().optional().nullable(),
    })
    .refine(
      data =>
        data.endDate == undefined ||
        data.startDate == undefined ||
        (data.endDate != undefined && data.startDate != undefined && data.endDate > data.startDate),
      {
        message: t('endStartDateError', { ns: 'common' }),
        path: ['endDate'],
      },
    )

  const form = useZodForm(timeReportsFilterSchema, {
    defaultValues: { locality: [], resource: [], timeReportType: [] },
  })

  return (
    <Box width={'100%'}>
      <AppForm form={form} onSubmit={onChange}>
        <Grid container>
          <Grid container md={11}>
            <Grid item md={2}>
              <FormSelectField
                label={t('locality', { ns: 'common' })}
                variant="outlined"
                getOptionLabel={row => row.name}
                data={sortedLocalities}
                name={'locality'}
                multiple
              />
            </Grid>
            <Grid item md={4}>
              <FormSelectField
                variant="outlined"
                getOptionLabel={row => row.name}
                label={t('resource', { ns: 'task' })}
                data={sortedResources}
                name={'resource'}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                multiple
              />
            </Grid>
            <Grid item md={3}>
              <FormSelectField
                variant="outlined"
                getOptionLabel={row => row.typeName}
                label={t('timeReportType', { ns: 'time-report-types' })}
                data={sortedTimeReportTypes}
                name={'timeReportType'}
                multiple
              />
            </Grid>
            <Grid item md={3}>
              <Grid container>
                <Grid item md={6}>
                  <BasicDatePicker name="startDate" label={t('startDate', { ns: 'common' })} />
                </Grid>
                <Grid item md={6}>
                  <BasicDatePicker name="endDate" label={t('endDate', { ns: 'common' })} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item md={1} paddingTop={'10px'}>
            <FormSubmitButton createText={t('filter', { ns: 'common' })} />
          </Grid>
        </Grid>
      </AppForm>
    </Box>
  )
}

export default TimeReportFilter
